<script setup>
  import {computed, ref, watch} from "vue";

  import ComponentDialog from "@components/Dialogs/Dialog.vue";
  import ComponentInput from "@components/Inputs/Input.vue";
  import LayoutPrintRecoveryCode from "@components/Layout/Components/PrintRecoveryCode.vue";
  import {MedButton, MedCheckbox, MedFormField, MedLabel, MedLink, MedSpinner} from "@pharma4u/medicheck-components";

  import {useAuthHttp} from "@/resources/js/Composable/useAuthHttp.js";

  defineOptions({
    name: "CryptDialogsDefault",
  });

  const props = defineProps({
    privacy: {
      type: Object,
      default: () => ({}),
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    logout: {
      type: Function,
      default: () => ({}),
    },
  });

  const emit = defineEmits(["success"]);

  const refCryptDialog = ref(null);

  const passwordPlaintext = ref("");
  const rememberPassword = ref(false);

  const valid = computed(() => validPasswordMinLength.value && props.privacy.getErrorMessage() === null);

  const validPasswordMinLength = computed(
    () => passwordPlaintext.value.trim().length >= props.privacy.getPasswordMinLength(),
  );

  watch(
    () => passwordPlaintext.value,
    (newValue) => {
      props.privacy.plaintextPassword(newValue);
    },
  );

  const open = () => {
    refCryptDialog.value.open();
  };

  const close = () => {
    if (refCryptDialog.value) refCryptDialog.value.close();
  };

  async function encryptPayload(payload) {
    const entries = Object.entries(payload);

    const encryptedEntries = await Promise.all(
      entries.map(async ([key, value]) => {
        const encryptedValue = await props.privacy.encryptValue(value);
        return [key, encryptedValue];
      }),
    );

    return Object.fromEntries(encryptedEntries);
  }

  const verifyPassword = (callback, errorCallback) => {
    callback =
      typeof callback === "function"
        ? callback
        : () => {
            props.privacy.setCurrentStatus("VERIFICATION_PENDING");
            props.privacy.initCrypt();
            props.privacy.setEncryptLocalStorage(rememberPassword.value ?? null);
            props.privacy.decryptStaticContent();

            useAuthHttp().post(route("api.user-api.refresh"));

            // success: data privacy password valid
            emit("success");

            setTimeout(() => {
              // check if there is pending data to process
              useAuthHttp()
                .get(route("api.pending-data-processing.index"))
                .then((response) => {
                  if (response?.length) {
                    // encrypt payload...
                    Promise.all(
                      response.map(async (d) => {
                        d.payload = await encryptPayload(d.payload);
                        return d;
                      }),
                    ).then((data) => {
                      // ...and send back to endpoint
                      useAuthHttp({data: data}).post(route("api.pending-data-processing.store"));
                    });
                  }
                });

              // always
              close();
            }, 800);
          };

    errorCallback =
      typeof errorCallback === "function"
        ? errorCallback
        : () => {
            props.privacy.setErrorMessage("Das Passwort ist nicht korrekt.");
            props.privacy.setCurrentStatus("VERIFICATION_INVALID");
          };

    props.privacy.verifyPassword(callback, errorCallback);
  };

  const userHasForgottenPassword = () => {
    passwordPlaintext.value = "";
    props.privacy.setErrorMessage(null);
    props.privacy.setCurrentStatus("RECOVERY");
  };

  const reprintRecoveryCode = () => {
    verifyPassword(() => {
      setTimeout(() => {
        printRecoveryCode();
      }, 1000);
    });
  };

  const printRecoveryCode = () => {
    globalThis.print();
    props.privacy.setRecoveryCodeHasBeenPrinted(true);
  };

  defineExpose({
    open,
    close,
  });
</script>

<template>
  <ComponentDialog ref="refCryptDialog" dialog-width="medium" :test-id="getTestId('default')">
    <template #title>
      <span class="hidden print:inline">MediCheck</span>
      Datenschutzpasswort
    </template>

    <template #content>
      <div class="print:hidden">
        <template v-if="isLoading">
          <div class="text-center">
            <MedSpinner size="3xl" :data-test="getTestId('default-dialog-spinner')" />
          </div>
        </template>

        <template v-else>
          <p>
            Bitte geben Sie Ihr Datenschutzpasswort ein, um personenbezogene Daten lokal (clientseitig / im Browser) zu
            entschlüsseln.
          </p>

          <form autocomplete="off" @submit.prevent="verifyPassword">
            <ComponentInput
              v-model="passwordPlaintext"
              class="mt-4"
              type="password"
              label="Passwort"
              autocomplete="off"
              :validation="privacy.getErrorMessage()"
              :maxlength="64"
              :show-maxlength-count="false"
              :show-max-length-message="'Die maximal mögliche Zeichenanzahl beträgt 64 Zeichen.'"
              :test-id="getTestId('default-dialog-password-plaintext')"
              @keyup.enter="verifyPassword"
            />
          </form>

          <p class="mt-6">
            Das Datenschutzpasswort (mind. 8 Zeichen lang) haben Sie separat festgelegt.
            <br />
            Hinweis: Mitarbeiter von pharma4u (MediCheck) haben keine Möglichkeit, diese Daten zu lesen oder
            wiederherzustellen.
            <MedLink
              as="button"
              variant="primary"
              data-test="layout-crypt-default-dialog-password-forgotten-link"
              @click.prevent="userHasForgottenPassword()"
            >
              Ich habe mein Passwort vergessen
            </MedLink>
          </p>

          <p class="mt-4 flex items-center space-x-4">
            <MedFormField orientation="horizontal">
              <MedCheckbox
                v-model="rememberPassword"
                variant="primary"
                :data-test="getTestId('default-dialog-remember-me-checkbox')"
              />

              <MedLabel>Datenschutzpasswort bis zum nächsten Logout speichern</MedLabel>
            </MedFormField>
          </p>
        </template>
      </div>

      <LayoutPrintRecoveryCode :recovery-code="privacy.getRecoveryCode()" :test-id="getTestId('default-dialog')" />
    </template>

    <template #actions>
      <MedButton
        :disabled="!valid || privacy.hasCryptStatus('PENDING')"
        variant="primary"
        :data-test="getTestId('default-dialog-verify-password-button')"
        @click="verifyPassword"
      >
        Entschlüsseln
      </MedButton>

      <template v-if="valid">
        <MedButton :data-test="getTestId('default-dialog-print-code-again-button')" @click="reprintRecoveryCode">
          Erneut drucken
        </MedButton>
      </template>

      <MedButton
        flat
        :variant="valid ? 'primary' : 'default'"
        :data-test="getTestId('default-dialog-logout-button')"
        @click="logout"
      >
        Abmelden
      </MedButton>
    </template>
  </ComponentDialog>
</template>
